"use client";

import { useEffect, useState, useCallback, useRef } from "react";
import { Moon, Sun } from "lucide-react";
import { Button } from "@/components/ui/button";


export function ThemeToggle() {
    const [theme, setTheme] = useState<"light" | "dark">("light");
    const [mounted, setMounted] = useState(false);
    const buttonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        setMounted(true);
        // Check localStorage or system preference
        const savedTheme = localStorage.getItem("theme") as "light" | "dark" | null;
        if (savedTheme) {
            setTheme(savedTheme);
            document.documentElement.classList.toggle("dark", savedTheme === "dark");
        } else {
            // Default to light
            setTheme("light");
            document.documentElement.classList.remove("dark");
        }
    }, []);

    const toggleTheme = useCallback(
        async (e: React.MouseEvent<HTMLButtonElement> | React.TouchEvent<HTMLButtonElement>) => {
            const newTheme = theme === "light" ? "dark" : "light";

            let x: number, y: number;

            if ("touches" in e && e.touches.length > 0) {
                x = e.touches[0].clientX;
                y = e.touches[0].clientY;
            } else if ("clientX" in e) {
                x = e.clientX;
                y = e.clientY;
            } else {
                const rect = buttonRef.current?.getBoundingClientRect();
                x = rect ? rect.left + rect.width / 2 : window.innerWidth / 2;
                y = rect ? rect.top + rect.height / 2 : window.innerHeight / 2;
            }

            const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
            const supportsViewTransitions = typeof document.startViewTransition === "function";

            if (!supportsViewTransitions || prefersReducedMotion) {
                setTheme(newTheme);
                localStorage.setItem("theme", newTheme);
                document.documentElement.classList.toggle("dark", newTheme === "dark");
                return;
            }

            const endRadius = Math.hypot(
                Math.max(x, window.innerWidth - x),
                Math.max(y, window.innerHeight - y)
            );

            const transition = document.startViewTransition!(() => {
                setTheme(newTheme);
                localStorage.setItem("theme", newTheme);
                document.documentElement.classList.toggle("dark", newTheme === "dark");
            });

            try {
                await transition.ready;

                document.documentElement.animate(
                    {
                        clipPath: [
                            `circle(0px at ${x}px ${y}px)`,
                            `circle(${endRadius}px at ${x}px ${y}px)`,
                        ],
                    },
                    {
                        duration: 500,
                        easing: "ease-in-out",
                        pseudoElement: "::view-transition-new(root)",
                    }
                );
            } catch {
            }
        },
        [theme]
    );

    if (!mounted) {
        return (
            <Button variant="ghost" size="icon" className="w-9 h-9">
                <Sun className="h-4 w-4" />
            </Button>
        );
    }

    return (
        <Button
            ref={buttonRef}
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            className="w-9 h-9 cursor-pointer"
        >
            {theme === "light" ? (
                <Moon className="h-4 w-4" />
            ) : (
                <Sun className="h-4 w-4" />
            )}
            <span className="sr-only">Toggle theme</span>
        </Button>
    );
}
